<template>
  <div class="stmode" :class="{ 'stmode--locked': !entitled }">
    <div class="stmode__row">
      <label class="stmode__switch">
        <input
          type="checkbox"
          :checked="modelValue === 'optimise'"
          :disabled="!entitled"
          @change="onToggle($event.target.checked)"
        >
        <span class="track"></span>
      </label>
      <div class="stmode__body">
        <div class="stmode__title">
          Send at each contact's best time within the window
          <span v-if="!entitled" class="stmode__hint">Available on Growth</span>
        </div>
        <div class="stmode__note">
          <template v-if="modelValue === 'optimise' && entitled">
            <CygnusIcon name="bolt" class="me-1" />Each contact is scheduled at the hour they most often open or click — always kept inside your send window. <strong>Still learning:</strong> contacts without enough history send on the window schedule.
          </template>
          <template v-else>
            Learn when each contact tends to engage (opens &amp; clicks) and send at their best hour, clamped into your send window. Contacts without enough history fall back to the window schedule.
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * SendTimingToggle — STO W2. The per-campaign / per-drip send-time MODE switch
 * that sits beside the SendWindowEditor. `optimise` schedules each contact at
 * their learned best time WITHIN the resolved send window; `window` is today's
 * window-only behaviour. Feature-gated: a non-entitled tenant sees the toggle
 * disabled with a violet "Available on Growth" hint (the server is the real gate
 * — an optimise write from a non-entitled tenant 422s).
 */
export default {
  name: 'SendTimingToggle',
  props: {
    // 'window' | 'optimise'
    modelValue: { type: String, default: 'window' },
    // Whether this tenant holds the send_time_optimisation feature flag.
    entitled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  methods: {
    onToggle(on) {
      if (!this.entitled) return; // disabled → no-op (server enforces too)
      this.$emit('update:modelValue', on ? 'optimise' : 'window');
    },
  },
};
</script>

<style>
.stmode { border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px); padding: 12px 14px; background: var(--m-bg-subtle, #F8FAFC); }
.stmode__row { display: flex; align-items: flex-start; gap: 12px; }
.stmode__switch { position: relative; width: 40px; height: 22px; flex: none; margin-top: 1px; }
.stmode__switch input { opacity: 0; width: 0; height: 0; }
.stmode__switch .track { position: absolute; inset: 0; background: #CBD2DE; border-radius: 999px; transition: 0.15s; cursor: pointer; }
.stmode__switch .track::before { content: ""; position: absolute; left: 2px; top: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; transition: 0.15s; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.stmode__switch input:checked + .track { background: var(--m-accent, #4F46E5); }
.stmode__switch input:checked + .track::before { transform: translateX(18px); }
.stmode__switch input:disabled + .track { cursor: not-allowed; opacity: 0.6; }
.stmode__title { font-weight: 600; font-size: 0.86rem; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.stmode__note { font-size: 0.78rem; color: var(--m-text-secondary, #697086); margin-top: 3px; line-height: 1.45; }
.stmode__hint {
  font-size: 0.68rem; font-weight: 700; letter-spacing: 0.02em; text-transform: uppercase;
  color: var(--m-violet, #7C3AED); background: var(--m-violet-soft, #F3EEFF);
  border: 1px solid var(--m-violet-border, #E4D7FB); border-radius: 100px; padding: 1px 9px;
}
.stmode--locked .stmode__title { color: var(--m-text-secondary, #697086); }
</style>
